import { t } from '@trimble/i18n';
import {
  TreeItemContextMenuOption,
  MultiSelectProps,
  SelectionTree,
  SelectionTreeProps,
  SingleSelectProps,
  TreeNodeDefinition,
  ChildrenLoadStatus,
} from '@trimble/ui-components/solidjs';
import { createEffect, createMemo, JSX, onCleanup } from 'solid-js';
import { createStore } from 'solid-js/store';
import { LoadableItems, LoadState } from '@trimble/data-loading';
import {
  createProjectDataSelection,
  FilterHandlerRecord,
  useProjectDataPanel,
} from '@trimble/project-data-panel-ui';
import { UnreachableCaseError } from 'ts-essentials';
import type { FileFolderItem } from '@trimble/project-data-panel-types';

const SUPPORTED_FILE_EXTENSIONS = [
  'dgn',
  'dwg',
  'dxf',
  'ifc',
  'nwc',
  'nwd',
  'rvt',
  'skp',
  'tekla',
  'tflx',
  'trb',
  'ttm',
  'vcl',
];

const ROOT_NODE_ID = 'tcModels';

export interface ModelTreeProps {
  tcFileFolder: LoadableItems<FileFolderItem>;
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  onCancelVisibilityToggle?(nodeId: string): void;
  onFitToView?(item: FileFolderItem): void;
  onShowOnly?(item: FileFolderItem): void;
  onSeeDetails?(item: FileFolderItem): void;
  onAddToModelSpace?(item: FileFolderItem): void;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<FileFolderItem>,
    e: MouseEvent
  ): void;
  multiSelect?: boolean;
}

export const ModelTree = (props: ModelTreeProps): JSX.Element => {
  const {
    filterText,
    sortBy,
    sortDirection,
    filterMode,
    selection: panelSelection,
  } = useProjectDataPanel();
  const selection = panelSelection ?? createProjectDataSelection();

  const [selectionTreeProps, setSelectionTreeProps] = createStore<
    Pick<SelectionTreeProps<FileFolderItem>, 'childrenLoadStatus' | 'nodes'>
  >({
    childrenLoadStatus: 'pending',
    nodes: [],
  });

  const isModelNodeLoading = (nodeId: string): boolean => {
    return nodeId === ROOT_NODE_ID ? false : props.isNodeLoading(nodeId);
  };

  const isModelNodeVisible = (nodeId: string): boolean => {
    return nodeId === ROOT_NODE_ID ? false : props.isNodeVisible(nodeId);
  };

  const isModelNodeDisabled = (nodeId: string): boolean => {
    const tcFileFolder = props.tcFileFolder;
    if (tcFileFolder.state !== LoadState.LoadComplete) return false;
    const item = tcFileFolder.items[nodeId];
    return (
      item !== undefined &&
      item.type !== 'FOLDER' &&
      !isFileTypeSupported(item.name)
    );
  };

  const ownsModelNode = (nodeId: string): boolean => {
    const tcFileFolder = props.tcFileFolder;
    return (
      nodeId !== ROOT_NODE_ID &&
      tcFileFolder.state === LoadState.LoadComplete &&
      nodeId in tcFileFolder.items
    );
  };

  const sectionHandle = selection.registerSection({
    ownsNode: ownsModelNode,
    isNodeVisible: isModelNodeVisible,
    onVisibilityToggle: (nodeId) => props.onVisibilityToggle(nodeId),
    isNodeDisabled: isModelNodeDisabled,
    isNodeLoading: isModelNodeLoading,
  });
  onCleanup(() => sectionHandle.dispose());

  const selectionProps = createMemo<
    SingleSelectProps<FileFolderItem> | MultiSelectProps
  >(() =>
    props.multiSelect
      ? {
          multiSelect: true,
          isNodeSelected: selection.isNodeSelected,
          onSelectionChange: (ids) => sectionHandle.applySelection(ids),
        }
      : {
          multiSelect: false,
          isNodeSelected: props.isNodeSelected,
          onNodeSelect: props.onNodeSelect,
        }
  );

  const createContextMenuOptions = (
    item: FileFolderItem
  ): TreeItemContextMenuOption[] => {
    const options: TreeItemContextMenuOption[] = [
      {
        text: t('siteVisualization.sidePanelLayout.fitView'),
        onClick: () => {
          props.onFitToView?.(item);
        },
        disabled: () =>
          !isModelNodeVisible(item.id) || isModelNodeLoading(item.id),
      },
      {
        text: t('siteVisualization.sidePanelLayout.showOnly'),
        onClick: () => {
          props.onShowOnly?.(item);
        },
      },
    ];

    if (props.onSeeDetails) {
      options.push({
        text: t('siteVisualization.sidePanelLayout.seeDetails'),
        onClick: () => {
          props.onSeeDetails?.(item);
        },
      });
    }

    if (props.onAddToModelSpace) {
      options.push({
        text: t('siteVisualization.sidePanelLayout.addToModelSpace'),
        onClick: () => {
          props.onAddToModelSpace?.(item);
        },
      });
    }

    return options;
  };

  const isFileTypeSupported = (fileName: string): boolean => {
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    return fileExtension
      ? SUPPORTED_FILE_EXTENSIONS.includes(fileExtension)
      : false;
  };

  const treeNodes = createMemo((): TreeNodeDefinition<FileFolderItem>[] => {
    const tcFileFolder = props.tcFileFolder;
    if (tcFileFolder.state !== LoadState.LoadComplete) return [];

    const items = Object.values(tcFileFolder.items);
    const toggleTooltip = t('mobileMapping.projectData.toggleModelVisibility');

    const rootNode: TreeNodeDefinition<FileFolderItem> = {
      id: ROOT_NODE_ID,
      label: t('mobileMapping.projectData.models'),
      labelIcon: 'cube',
      isLeaf: false,
    };

    const itemNodes = items.map((item) => {
      const isFolder = item.type === 'FOLDER';
      return {
        id: item.id,
        label: item.name,
        data: item,
        parentId: item.rootElement ? ROOT_NODE_ID : item.parentId,
        isLeaf: !isFolder,
        status:
          !isFolder && !isFileTypeSupported(item.name)
            ? { disabled: true }
            : undefined,
        toggleTooltip: isFolder ? undefined : toggleTooltip,
        contextMenuOptions: isFolder ? [] : createContextMenuOptions(item),
      } satisfies TreeNodeDefinition<FileFolderItem>;
    });

    return [rootNode, ...itemNodes];
  });

  const filterHandlers: FilterHandlerRecord<FileFolderItem> = {
    all: { filter: () => true },
    active: { filter: (node) => isModelNodeVisible(node.id) },
    archived: { filter: () => false },
  };

  createEffect(() => {
    const tcFileFolder = props.tcFileFolder;
    const loadState = tcFileFolder.state;
    let childrenLoadStatus: ChildrenLoadStatus['childrenLoadStatus'];

    switch (loadState) {
      case LoadState.Loading:
      case LoadState.NotLoaded:
        childrenLoadStatus = 'pending';
        break;
      case LoadState.Error:
        childrenLoadStatus = 'error';
        break;
      case LoadState.LoadComplete:
        childrenLoadStatus = 'success';
        break;
      default:
        throw new UnreachableCaseError(loadState);
    }

    setSelectionTreeProps({
      childrenLoadStatus,
      nodes: treeNodes(),
    });
  });

  const toggleModelVisibility = (nodeId: string): void => {
    if (nodeId === ROOT_NODE_ID) return;
    if (props.multiSelect) {
      selection.onVisibilityToggle(nodeId);
    } else {
      props.onVisibilityToggle(nodeId);
    }
  };

  const cancelModelVisibilityToggle = (nodeId: string): void => {
    if (nodeId !== ROOT_NODE_ID) {
      props.onCancelVisibilityToggle?.(nodeId);
    }
  };

  return (
    <div class="w-full" data-testid="model-selection-tree">
      <SelectionTree
        {...selectionTreeProps}
        sortBy={sortBy()}
        sortDirection={sortDirection()}
        filterText={filterText()}
        filterMode={filterMode()}
        filterHandlers={filterHandlers}
        isNodeLoading={isModelNodeLoading}
        isNodeVisible={isModelNodeVisible}
        onVisibilityToggle={toggleModelVisibility}
        onCancelVisibilityToggle={cancelModelVisibilityToggle}
        {...selectionProps()}
      />
    </div>
  );
};
